import {
  AddCircle,
  CancelOutlined,
  DynamicForm,
  RemoveCircle,
  CheckCircle,
  HourglassEmpty,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL;
const ENV_OPTIONS = ["QA", "PROD", "STAGING"];

export default function ManageReleases() {
  const navigate = useNavigate();
  const { index } = useParams();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [editingReleaseIndex, setEditingReleaseIndex] = useState(null);
  const [editingAreaIndex, setEditingAreaIndex] = useState(null);

  const [formName, setFormName] = useState("");
  const [formEnvironments, setFormEnvironments] = useState("");
  const [formUsers, setFormUsers] = useState([]);
  const [areaData, setAreaData] = useState(null);
  const [allAreaData, setAllAreaData] = useState(null);
  const [selectedAreaIndex, setSelectedAreaIndex] = useState("");

  useEffect(() => {
    fetchAllAreas();
  }, []);

  useEffect(() => {
    if (selectedAreaIndex !== "") {
      fetchAreas(selectedAreaIndex);
    } else {
      setAreaData(null);
    }
  }, [selectedAreaIndex]);

  async function fetchAreas(areaIdx = selectedAreaIndex) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/areas/${areaIdx}/releases`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch: ${res.status} ${res.statusText}`
        );
      }
      const data = await res.json();
      console.log("--------------------", data);
      setAreaData(data);
    } catch (err) {
      console.error("Error fetching areas:", err);
      setError(err.message || "Failed to fetch areas");
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllAreas() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/areas`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch: ${res.status} ${res.statusText}`
        );
      }
      const data = await res.json();
      setAllAreaData(data);
    } catch (err) {
      console.error("Error fetching areas:", err);
      setError(err.message || "Failed to fetch areas");
    } finally {
      setLoading(false);
    }
  }

  const openDialog = (
    release = null,
    areaIndex = null,
    releaseIndex = null
  ) => {
    if (release) {
      setEditingAreaIndex(areaIndex);
      setEditingReleaseIndex(releaseIndex);
      setFormName(release.name);
      setFormEnvironments(
        Array.isArray(release.environments) && release.environments.length > 0
          ? release.environments[0]
          : ""
      );
      setFormUsers(Array.isArray(release.users) ? [...release.users] : []);
    } else {
      setEditingAreaIndex(null);
      setEditingReleaseIndex(null);
      setFormName("");
      setFormEnvironments("");
      setFormUsers([{ userId: "user1", name: "", email: "" }]);
    }
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingReleaseIndex(null);
    setEditingAreaIndex(null);
    setFormName("");
    setFormEnvironments("");
    setFormUsers([]);
  };

  const handleEnvironmentSelect = (event) => {
    setFormEnvironments(event.target.value);
  };

  const addUser = () => {
    const newUserId = `user${formUsers.length + 1}`;
    setFormUsers([...formUsers, { userId: newUserId, name: "", email: "" }]);
  };

  const removeUser = (userId) => {
    setFormUsers(formUsers.filter((u) => u.userId !== userId));
  };

  const updateUserField = (userId, field, value) => {
    setFormUsers(
      formUsers.map((u) => (u.userId === userId ? { ...u, [field]: value } : u))
    );
  };

  const validateForm = () => {
    let isValid = true;
    let message = "";

    if (!formName.trim()) {
      message = "Release name is required";
      isValid = false;
    } else if (!formEnvironments) {
      message = "Environment is required";
      isValid = false;
    } else if (formUsers.length === 0) {
      message = "At least one user is required";
      isValid = false;
    } else {
      // Validate all users have name and email
      for (const user of formUsers) {
        if (!user.name.trim() || !user.email.trim()) {
          message = "All users must have name and email";
          isValid = false;
          break;
        }

        // Basic email validation
        if (user.email.trim() && !user.email.includes("@")) {
          message = "Please enter valid email addresses";
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      alert(message);
    }
    return isValid;
  };

  // Submit form: call POST or PUT API
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const payload = {
      name: formName.trim(),
      environments: [formEnvironments],
      users: formUsers.map((user) => ({
        ...user,
        name: user.name.trim(),
        email: user.email.trim(),
      })),
    };

    try {
      let res;
      if (editingReleaseIndex !== null && editingAreaIndex !== null) {
        // Update existing release in area
        res = await fetch(
          `${API_BASE}/areas/${index}/releases/${editingReleaseIndex}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
      } else {
        // For create, use the selectedAreaIndex instead of index from URL params
        const targetAreaIndex =
          selectedAreaIndex !== "" ? selectedAreaIndex : 0;
        res = await fetch(`${API_BASE}/areas/${targetAreaIndex}/releases`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to save: ${res.status} ${res.statusText}`
        );
      }

      await fetchAreas(selectedAreaIndex);
      closeDialog();
    } catch (err) {
      console.error("Error saving release:", err);
      setError(err.message || "Failed to save release");
      alert(err.message || "Failed to save release");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelease = async (releaseIndex) => {
    if (!window.confirm("Are you sure you want to delete this release?")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_BASE}/areas/${index}/releases/${releaseIndex}`,
        {
          method: "DELETE",
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to delete: ${res.status} ${res.statusText}`
        );
      }

      await fetchAreas();
    } catch (err) {
      console.error("Error deleting release:", err);
      setError(err.message || "Failed to delete release");
      alert(err.message || "Failed to delete release");
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle direct navigation to deployment plan
  const handleViewDeploymentPlan = (releaseName) => {
    const targetAreaIndex = index !== undefined ? index : 0;
    navigate(`/areas/${targetAreaIndex}/deployment/${releaseName}`);
  };

  const handleStartDeployment = (releaseId, releaseName) => {
    const targetAreaIndex = selectedAreaIndex !== "" ? selectedAreaIndex : 0;
    navigate(
      `/areas/${targetAreaIndex}/tracker/${releaseId}?releaseName=${releaseName}`
    );
  };

  // Process releases from area data
  let releases =
    areaData?.releases?.map((release, releaseIndex) => ({
      ...release,
      areaIndex: areaData.areaIndex,
      releaseIndex,
      areaName: areaData.areaName,
    })) || [];

  const today = moment.tz("America/New_York").format("YYYY-MM-DD");
  const pendingReleases = releases
    .filter((r) => r.deploymentDate ? r.deploymentDate >= today : (!r.createdAt || new Date(r.createdAt) >= new Date()))
    .sort((a, b) => (a.deploymentDate || "").localeCompare(b.deploymentDate || ""));
  const completedDateReleases = releases
    .filter((r) => r.deploymentDate ? r.deploymentDate < today : (r.createdAt && new Date(r.createdAt) < new Date()))
    .sort((a, b) => (b.deploymentDate || "").localeCompare(a.deploymentDate || ""));

  const totalReleases = releases.length;

  // Compact release name button
  const ReleaseName = ({ release }) => (
    <Tooltip title={`Open tracker for ${release.name}`} arrow>
      <Button
        variant="text"
        color="primary"
        size="small"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8rem",
          py: 0.25,
          px: 1,
          minWidth: 0,
          borderRadius: 1,
          backgroundColor: "rgba(63,81,181,0.06)",
          "&:hover": { backgroundColor: "rgba(63,81,181,0.14)" },
          lineHeight: 1.4,
        }}
        onClick={() =>
          handleStartDeployment(release.releaseId, release.name)
        }
      >
        {release.name}
      </Button>
    </Tooltip>
  );

  // Compact env chips
  const EnvChips = ({ environments }) =>
    environments && environments.length > 0 ? (
      environments.map((env) => (
        <Chip
          key={env}
          label={env}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mr: 0.3, height: 20, fontSize: "0.7rem" }}
        />
      ))
    ) : (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );

  // Compact team display
  const TeamMembers = ({ users }) =>
    users && users.length > 0 ? (
      <Box sx={{ display: "flex", gap: 0.3, flexWrap: "wrap" }}>
        {users.slice(0, 3).map((user) => (
          <Tooltip key={user.userId} title={user.email || user.name} arrow>
            <Avatar
              sx={{
                width: 22,
                height: 22,
                fontSize: "0.65rem",
                bgcolor: "primary.main",
              }}
            >
              {user.name?.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
        ))}
        {users.length > 3 && (
          <Tooltip
            title={users
              .slice(3)
              .map((u) => u.name)
              .join(", ")}
            arrow
          >
            <Avatar
              sx={{
                width: 22,
                height: 22,
                fontSize: "0.6rem",
                bgcolor: "grey.400",
              }}
            >
              +{users.length - 3}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    ) : (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );

  // Dense table cell style
  const denseCell = { py: 0.5, px: 1, fontSize: "0.8rem", lineHeight: 1.3 };
  const denseHeaderCell = {
    py: 0.75,
    px: 1,
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#283593",
    borderBottom: "2px solid #9fa8da",
    whiteSpace: "nowrap",
    background: "linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)",
  };

  // Section header component
  const SectionHeader = ({ icon, title, count, color }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: `2px solid ${color}`,
        background: `${color}08`,
      }}
    >
      {icon}
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color, flex: 1, fontSize: "0.85rem" }}
      >
        {title}
      </Typography>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          fontSize: "0.7rem",
          fontWeight: 700,
          bgcolor: `${color}18`,
          color,
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f5f6fa", p: 2, minHeight: "100vh" }}>
      {/* Header Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#1a237e" }}
        >
          {areaData?.areaName
            ? `${areaData.areaName} — Releases`
            : "Releases"}
        </Typography>
        <Box sx={{ width: 260 }}>
          <TextField
            select
            label="Select Area"
            value={selectedAreaIndex}
            onChange={(e) => setSelectedAreaIndex(e.target.value)}
            fullWidth
            size="small"
            disabled={loading || !allAreaData}
            sx={{ bgcolor: "#fff", borderRadius: 1 }}
          >
            {(allAreaData || []).map((area, idx) => (
              <MenuItem key={idx} value={idx}>
                {area.areaName}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress thickness={4} size={36} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {selectedAreaIndex !== "" &&
        !loading &&
        totalReleases === 0 && (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed #ccc",
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {!areaData ? "Area not found" : "No releases found"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!areaData
                ? "The selected area could not be found."
                : "No releases found for this area."}
            </Typography>
          </Paper>
        )}

      {/* Tables in a 2-column grid for max data density */}
      {selectedAreaIndex !== "" && !loading && totalReleases > 0 && (
        <Grid container spacing={2}>
          {/* Upcoming Releases */}
          {pendingReleases.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                 <SectionHeader
                      icon={<HourglassEmpty sx={{ fontSize: 18, color: "#e65100" }} />}
                      title="Upcoming Releases"
                      count={pendingReleases.length}
                      color="#e65100"
                    />
                <TableContainer sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={denseHeaderCell}>Name</TableCell>
                        <TableCell sx={denseHeaderCell}>Deployment Date</TableCell>
                        <TableCell sx={denseHeaderCell}>Env</TableCell>
                        <TableCell sx={denseHeaderCell}>Team</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingReleases.map((release) => (
                        <TableRow
                          key={release.releaseId || release.releaseIndex}
                          hover
                        >
                          <TableCell sx={denseCell}>
                            <ReleaseName release={release} />
                          </TableCell>
                          <TableCell sx={{ ...denseCell, whiteSpace: "nowrap" }}>
                            {release.deploymentDate || "—"}
                          </TableCell>
                          <TableCell sx={denseCell}>
                            <EnvChips environments={release.environments} />
                          </TableCell>
                          <TableCell sx={denseCell}>
                            <TeamMembers users={release.users} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Completed Releases */}
          {completedDateReleases.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <SectionHeader
                      icon={<CheckCircle sx={{ fontSize: 18, color: "#1b5e20" }} />}
                      title="Completed Releases"
                      count={completedDateReleases.length}
                      color="#1b5e20"
                    />
                <TableContainer sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={denseHeaderCell}>Name</TableCell>
                        <TableCell sx={denseHeaderCell}>Deployment Date</TableCell>
                        <TableCell sx={denseHeaderCell}>Env</TableCell>
                        <TableCell sx={denseHeaderCell}>Team</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completedDateReleases.map((release) => (
                        <TableRow
                          key={release.releaseId || release.releaseIndex}
                          hover
                        >
                          <TableCell sx={denseCell}>
                            <ReleaseName release={release} />
                          </TableCell>
                          <TableCell sx={{ ...denseCell, whiteSpace: "nowrap" }}>
                            {release.deploymentDate || "—"}
                          </TableCell>
                          <TableCell sx={denseCell}>
                            <EnvChips environments={release.environments} />
                          </TableCell>
                          <TableCell sx={denseCell}>
                            <TeamMembers users={release.users} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Dialog with improved UI */}
      <Dialog
        open={open}
        onClose={() => !loading && closeDialog()}
        maxWidth="md"
        fullWidth
        className="release-dialog"
      >
        <DialogTitle className="dialog-title">
          <DynamicForm className="dialog-icon" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            {editingReleaseIndex !== null
              ? "Edit Release"
              : "Create New Release"}
          </Typography>
          <IconButton
            className="dialog-close-btn"
            onClick={() => !loading && closeDialog()}
            disabled={loading}
            size="small"
            aria-label="close"
          >
            <CancelOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className="dialog-content">
          <Box className="form-section">
            <TextField
              label="Release Name"
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              margin="normal"
              disabled={loading}
              variant="outlined"
              className="form-input"
              placeholder="Enter a descriptive name for this release"
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Box>

          <Box className="form-section">
            <TextField
              select
              label="Select Environment"
              value={formEnvironments}
              onChange={handleEnvironmentSelect}
              fullWidth
              disabled={loading}
              variant="outlined"
              className="form-input"
              placeholder="Select environment"
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            >
              {ENV_OPTIONS.map((env) => (
                <MenuItem key={env} value={env}>
                  {env}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box className="form-section">
            <Typography variant="subtitle1" className="form-section-title">
              <AddCircle className="icon" fontSize="small" />
              Team Members
            </Typography>

            {formUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No team members added yet
              </Typography>
            )}

            {formUsers.map((user) => (
              <Paper
                key={user.userId}
                variant="outlined"
                className="user-form-item"
              >
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  {user.name ? user.name.charAt(0).toUpperCase() : "?"}
                </Avatar>

                <TextField
                  label="Team Member Name"
                  value={user.name}
                  onChange={(e) =>
                    updateUserField(user.userId, "name", e.target.value)
                  }
                  required
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  className="user-field-name"
                  placeholder="Full name"
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />

                <TextField
                  label="Email Address"
                  type="email"
                  value={user.email}
                  onChange={(e) =>
                    updateUserField(user.userId, "email", e.target.value)
                  }
                  required
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  className="user-field-email"
                  placeholder="email@example.com"
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />

                <IconButton
                  color="error"
                  onClick={() => removeUser(user.userId)}
                  disabled={formUsers.length === 1 || loading}
                  size="small"
                  className="remove-button"
                >
                  <RemoveCircle fontSize="small" />
                </IconButton>
              </Paper>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddCircle />}
              onClick={addUser}
              disabled={loading}
              className="add-user-button"
              sx={{ borderRadius: 2 }}
            >
              Add Team Member
            </Button>
          </Box>

          {error && (
            <Alert severity="error" className="error-message">
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions className="dialog-actions">
          <Button
            onClick={closeDialog}
            disabled={loading}
            variant="outlined"
            className="cancel-button"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            className="submit-button"
            sx={{ borderRadius: 2 }}
          >
            {loading ? (
              <CircularProgress size={20} className="submit-loading" />
            ) : null}
            {editingReleaseIndex !== null ? "Save Changes" : "Create Release"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
